import '@altibox/design-system-component-lib/dist/index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from 'styled-components';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';

import { Spinner } from 'src/components/spinner';
import { store } from 'src/state/configure-store';
import { GlobalStyle } from 'src/theme/global';
import { theme } from 'src/theme/theme';

import { App } from './components/app';
import {
  PageError,
  PageInFlightOrder,
  PageNoServiceLocations,
  PageNotAvailable,
  PageProductNotAvailable,
  PageRestrictedPurchase,
  PageSessionExpired,
  PageWrongRole,
} from './pages/error';
import { NavigationService } from './services/navigation-service';
import { selectHasAccountBeenSelected, selectHasNoAccounts, selectHasOnlyOneAccount } from './state/account/selectors';
import {
  selectHasNoServiceLocations,
  selectHasOnlyOneServiceLocation,
  selectIsAddressSelected,
  selectIsUserInInfonova,
} from './state/address/selectors';
import { selectAuthFailed } from './state/auth/selectors';
import { selectHasErrors } from './state/errors/selectors';
import { orderStartedOrFailed } from './state/order/selectors';
import { RequestType } from './state/request/types';
import {
  anyStartedOrFailedRequests,
  areRequestsReady,
  selectPurchaseEligibilityStatus,
} from './state/request/selectors';
import { PageLogout } from './pages/logout';
import { PageChooseAccount } from './pages/choose-account';
import { PageChooseServiceLocation } from './pages/choose-service-location';
import { PageAuthCallback } from './components/page-auth-callback';
import { Store } from './components/app/store';
import { ProtectedRoute } from './components/protected-order-route/protected-route';
import { ProtectedOrderRoute } from './components/protected-order-route/protected-order-route';
import { RootState } from './state/types';
import { negate } from './utils/functions';
import { PurchaseEligibilityStatus } from './api/@generated/backend';

const allowShowErrorPage = (state: RootState) =>
  anyStartedOrFailedRequests(state) ||
  selectAuthFailed(state) ||
  orderStartedOrFailed(state) ||
  (selectPurchaseEligibilityStatus(state) !== PurchaseEligibilityStatus.SUCCESS &&
    selectPurchaseEligibilityStatus(state) !== PurchaseEligibilityStatus.DUNNING) ||
  (areRequestsReady(state.request, RequestType.GET_ACCOUNTS) && selectHasNoAccounts(state)) ||
  selectHasErrors(state);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />} errorElement={<PageError />}>
      <Route path={NavigationService.AUTH_CALLBACK} element={<PageAuthCallback />} />
      <Route path={NavigationService.LOGOUT} element={<PageLogout />} />
      <Route path={NavigationService.ERROR_NOT_AVAILABLE} element={<PageNotAvailable />} />
      <Route path={NavigationService.ERROR_SESSION_EXPIRED} element={<PageSessionExpired />} />
      <Route element={<ProtectedRoute />}>
        <Route path={NavigationService.ERROR_PRODUCT_NOT_AVAILABLE} element={<PageProductNotAvailable />} />
      </Route>
      <Route element={<ProtectedOrderRoute />}>
        <Route path={NavigationService.ERROR_WRONG_ROLE} element={<PageWrongRole />} />
        <Route path={NavigationService.ERROR_RESTRICTED_PURCHASE} element={<PageRestrictedPurchase />} />
        <Route path={NavigationService.ERROR_NO_SERVICE_LOCATIONS} element={<PageNoServiceLocations />} />
        <Route path={NavigationService.ERROR_HAS_INFLIGHT_ORDER} element={<PageInFlightOrder />} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            guards={[
              {
                isProtected: negate(allowShowErrorPage),
                redirectTo: NavigationService.CHOOSE_ACCOUNT,
              },
            ]}
          />
        }
      >
        <Route path={NavigationService.ERROR} element={<PageError />} />
      </Route>
      <Route
        element={
          <ProtectedOrderRoute
            requests={[RequestType.GET_ACCOUNTS, RequestType.GET_PARTNERS]}
            guards={[
              {
                isProtected: selectHasNoAccounts,
                redirectTo: NavigationService.ERROR_NOT_AVAILABLE,
              },
              {
                isProtected: (state) => selectHasAccountBeenSelected(state) && selectHasOnlyOneAccount(state),
                redirectTo: NavigationService.CHOOSE_SERVICE_LOCATION,
              },
            ]}
          />
        }
      >
        <Route path={NavigationService.CHOOSE_ACCOUNT} element={<PageChooseAccount />} />
      </Route>
      <Route
        element={
          <ProtectedOrderRoute
            requests={[RequestType.GET_ACCOUNT_INFO]}
            guards={[
              {
                isProtected: negate(selectIsUserInInfonova),
                redirectTo: NavigationService.ERROR_NOT_AVAILABLE,
              },
              {
                isProtected: selectHasNoServiceLocations,
                redirectTo: NavigationService.ERROR_NO_SERVICE_LOCATIONS,
              },
              {
                isProtected: (state) => selectIsAddressSelected(state) && selectHasOnlyOneServiceLocation(state),
                redirectTo: NavigationService.STORE,
              },
            ]}
          />
        }
      >
        <Route path={NavigationService.CHOOSE_SERVICE_LOCATION} element={<PageChooseServiceLocation />} />
      </Route>
      <Route path="*" element={<Store />} />
    </Route>,
  ),
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistStore(store)} loading={<Spinner />}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <RouterProvider router={router} />
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);
